import { makeAutoObservable, runInAction } from 'mobx';

import type { MembershipCardChangeGateway } from '../api/MembershipCardChangeGateway';
import {
  type MembershipCardChangeEvent,
  type MembershipCardChangeExit,
  type MembershipCardChangeFailure,
  type MembershipCardChangeResult,
  toCardChangeFormReply,
} from '../domain/cardChange';
import { asManageError, type MembershipManageIncident } from '../domain/manage';
import type {
  CardFormReply,
  MembershipCardDetails,
  MembershipFormIncident,
} from '../domain/purchase';
import type { Membership, MembershipCard } from '../domain/types';
import {
  type CardForm,
  type CardFormFactory,
  type CardFormHandlers,
  CardFormLoadError,
  type CardPaymentData,
} from '../payment/CardForm';
import {
  type CardChangeErrorOutcome,
  cardChangeErrorOutcome,
} from './cardChangeOutcome';
import type { MembershipMarkers } from './MembershipMarkers';
import { formRuntimeIncident, formStartIncident } from './purchaseOutcome';
import { BUSY_PHASES, type MembershipPurchasePhase } from './purchaseTypes';

/** Everything the card store needs from the outside world. */
export interface MembershipCardChangeStoreDeps {
  gateway: MembershipCardChangeGateway;
  createCardForm: CardFormFactory;
  markers: MembershipMarkers;
  /** Absolute `returnUrl` for a redirect 3DS: the "Add card" sheet (`?modal=club-card`). */
  returnUrl: () => string;
  /** Courier zone of the current address; `null` while unknown. */
  zoneId: () => string | null;
  /**
   * The card is saved. `card` is the new card as the answer names it; `me`
   * is a `/me` answer to show as is, `null` when `/me` has to be re-read —
   * the membership inside a card change answer has no scheduled plan change
   * and no payment issue, so it is never shown. `restored` — an overdue
   * membership is `active` again by the answer's status, which is reliable.
   */
  onCardUpdated: (update: MembershipCardUpdate, restored: boolean) => void;
  /**
   * The overdue charge may still be running — the card is saved with a
   * `pending` answer, or the answer was lost while `/me` is still `past_due`;
   * the membership page waits for `active`. `me` is a `/me` answer when one
   * was read, `null` otherwise.
   */
  onRestorePending: (me: Membership | null) => void;
  /** The backend's membership changed under us (404, 409); re-read it. */
  onMembershipChanged: () => void;
  /** Reloads the page; the only retry for a card form whose code never arrived. */
  reloadPage: () => void;
  track: (event: MembershipCardChangeEvent) => void;
  report: (incident: MembershipManageIncident | MembershipFormIncident) => void;
}

/** What `onCardUpdated` reports about a saved card. */
export interface MembershipCardUpdate {
  card: MembershipCard | null;
  me: Membership | null;
}

/** The membership the card is changed for, as the request found it. */
interface CardChangeSubject {
  planCode: string;
  wasPastDue: boolean;
}

const FAILED: CardFormReply = { kind: 'failed' };
const IGNORED: CardFormReply = { kind: 'ignored' };

/**
 * The "Add card" sheet (`?modal=club-card`). Works like Join: the card and its confirmation are
 * sent exactly once, a second click, an Enter in a card field or a repeated
 * SDK callback is ignored while one is in flight, an unknown outcome is
 * checked through `/me` instead of being re-sent, and after a decline or a
 * failed confirmation the card form is recreated. A confirmation names the
 * `tokenId` of its change; before a bank redirect it is kept in a session
 * marker. `reset()` drops every answer still in flight.
 */
export class MembershipCardChangeStore {
  phase: MembershipPurchasePhase = 'idle';
  failure: MembershipCardChangeFailure | null = null;
  exit: MembershipCardChangeExit | null = null;
  isCardValid = false;
  /** Changes whenever the card form must be recreated; the UI keys the form slot by it. */
  formKey = 0;

  private subject: CardChangeSubject | null = null;
  private tokenId: string | null = null;
  private form: CardForm | null = null;
  private inFlight = false;
  /** The page unmounted while a request was in flight: once it settles, the store is forgotten. */
  private isLeft = false;
  private epoch = 0;
  /** The card form's code never arrived, so "Try again" has to reload the page. */
  private isReloadRequired = false;

  constructor(private readonly deps: MembershipCardChangeStoreDeps) {
    makeAutoObservable<
      MembershipCardChangeStore,
      | 'deps'
      | 'subject'
      | 'tokenId'
      | 'form'
      | 'inFlight'
      | 'isLeft'
      | 'epoch'
      | 'isReloadRequired'
    >(
      this,
      {
        deps: false,
        subject: false,
        tokenId: false,
        form: false,
        inFlight: false,
        isLeft: false,
        epoch: false,
        isReloadRequired: false,
      },
      { autoBind: true },
    );
  }

  /** A request is in flight or its outcome is being checked. */
  get isBusy(): boolean {
    return BUSY_PHASES.has(this.phase);
  }

  /** The card is valid and nothing is in flight. */
  get canSubmit(): boolean {
    return this.phase === 'form' && this.isCardValid;
  }

  /**
   * Opens the form for `membership`; a no-op once open or while a request is
   * in flight. A failure set before the form opened (a bank return without
   * its marker) stays on screen.
   */
  start(membership: Membership): void {
    if (this.inFlight || this.exit !== null || this.phase !== 'idle') {
      return;
    }
    this.subject = {
      planCode: membership.plan.code,
      wasPastDue: membership.status === 'past_due',
    };
    this.phase = 'form';
  }

  /** Mounts a fresh card form into `node`, replacing the previous one. */
  async mountForm(node: HTMLElement): Promise<void> {
    this.unmountForm();
    const form = this.deps.createCardForm();
    this.form = form;
    try {
      await form.mount(node, this.handlersFor(form));
    } catch (error) {
      if (this.form !== form) {
        return;
      }
      runInAction(() => {
        this.failure = 'form_unavailable';
        this.isReloadRequired = error instanceof CardFormLoadError;
      });
      this.deps.report(formStartIncident(error));
      this.trackFailure('form_unavailable');
    }
  }

  /** The form slot unmounted: a 3DS challenge shown in it is lost, so it ends on "not confirmed" with a fresh form. */
  releaseForm(): void {
    if (this.phase === 'challenge' && !this.inFlight) {
      this.fail('not_confirmed');
      return;
    }
    this.unmountForm();
  }

  unmountForm(): void {
    const form = this.form;
    this.form = null;
    this.isCardValid = false;
    form?.destroy();
  }

  /** "Try again" after the card form could not start; reloads the page when its code never arrived. */
  retryForm(): void {
    if (this.isReloadRequired) {
      this.deps.reloadPage();
      return;
    }
    this.failure = null;
    this.renewForm();
  }

  submit(): void {
    if (this.canSubmit) {
      this.form?.submit();
    }
  }

  /**
   * Continues a card change after the bank's redirect: confirms the one-time
   * `redirectResult` once, with the token this customer saved within the
   * last 30 minutes. Without that marker the result cannot be confirmed (the
   * confirmation names the token): `/me` is re-read and a fresh form asks
   * for the card again.
   */
  async resumeRedirect(redirectResult: string): Promise<void> {
    const context = this.deps.markers.readCardRedirect();
    this.deps.markers.clearCardRedirect();
    if (this.inFlight) {
      return;
    }
    if (!context) {
      this.loseRedirect();
      return;
    }
    const subject = {
      planCode: context.planCode,
      wasPastDue: context.wasPastDue,
    };
    this.subject = subject;
    this.tokenId = context.tokenId;
    this.exit = null;
    const reply = await this.confirm(
      context.tokenId,
      { redirectResult },
      subject,
      null,
    );
    if (reply.kind !== 'action') {
      return;
    }
    runInAction(() => {
      if (this.form) {
        this.form.handleAction(reply.action);
        return;
      }
      this.fail('not_confirmed');
    });
  }

  private loseRedirect(): void {
    this.exit = null;
    this.failure = 'redirect_lost';
    this.deps.track({
      name: 'cardUpdateFailed',
      payload: {
        planCode: this.subject?.planCode ?? null,
        reason: 'redirect_lost',
      },
    });
    this.deps.onMembershipChanged();
    if (this.phase !== 'idle') {
      this.renewForm();
    }
  }

  /** The "Add card" sheet (`?modal=club-card`) mounted: a request still settling from a previous visit now belongs to this one. */
  enter(): void {
    this.isLeft = false;
  }

  /**
   * The "Add card" sheet (`?modal=club-card`) unmounted: drops the form; an idle page is forgotten, one
   * in flight keeps settling and is forgotten once it does, so the next
   * visit starts from a fresh form, not from its failure or exit.
   */
  leave(): void {
    this.unmountForm();
    if (this.inFlight) {
      this.isLeft = true;
      return;
    }
    this.forget();
  }

  /** Logout: drops answers in flight, the form and the redirect marker. */
  reset(): void {
    this.epoch += 1;
    this.inFlight = false;
    this.unmountForm();
    this.forget();
    this.deps.markers.clearCardRedirect();
  }

  private forget(): void {
    this.isLeft = false;
    this.phase = 'idle';
    this.failure = null;
    this.isReloadRequired = false;
    this.exit = null;
    this.subject = null;
    this.tokenId = null;
  }

  private handlersFor(form: CardForm): CardFormHandlers {
    return {
      onSubmit: (data) => this.handleSubmit(form, data),
      onAdditionalDetails: (details) => this.handleDetails(form, details),
      onValidityChange: (isValid) => this.setCardValidity(form, isValid),
      onActionShown: () => undefined,
      onEnterKey: () => {
        if (this.form === form) {
          this.submit();
        }
      },
      onError: (error) => this.handleFormError(form, error),
    };
  }

  private setCardValidity(form: CardForm, isValid: boolean): void {
    if (this.form === form) {
      this.isCardValid = isValid;
    }
  }

  private async handleSubmit(
    form: CardForm,
    data: CardPaymentData,
  ): Promise<CardFormReply> {
    const { subject } = this;
    if (form !== this.form || this.inFlight || !this.canSubmit || !subject) {
      return IGNORED;
    }
    const epoch = this.epoch;
    this.inFlight = true;
    this.phase = 'submitting';
    this.failure = null;
    this.tokenId = null;
    try {
      const zoneId = this.deps.zoneId();
      const result = await this.deps.gateway.changeCard({
        paymentMethod: data.paymentMethod,
        browserInfo: data.browserInfo,
        origin: data.origin,
        returnUrl: this.deps.returnUrl(),
        ...(zoneId ? { zoneId } : {}),
      });
      return epoch === this.epoch ? this.settle(result, subject, form) : FAILED;
    } catch (error) {
      if (epoch === this.epoch) {
        await this.handleError(
          cardChangeErrorOutcome('submit', asManageError(error)),
          subject,
          epoch,
        );
      }
      return FAILED;
    }
  }

  private handleDetails(
    form: CardForm,
    details: MembershipCardDetails,
  ): Promise<CardFormReply> {
    const { subject, tokenId } = this;
    if (
      form !== this.form ||
      this.inFlight ||
      this.phase !== 'challenge' ||
      !subject ||
      !tokenId
    ) {
      return Promise.resolve(IGNORED);
    }
    return this.confirm(tokenId, details, subject, form);
  }

  /** `sender` is the form that asked for the confirmation, `null` for a bank return. */
  private async confirm(
    tokenId: string,
    details: MembershipCardDetails,
    subject: CardChangeSubject,
    sender: CardForm | null,
  ): Promise<CardFormReply> {
    const epoch = this.epoch;
    this.inFlight = true;
    this.phase = 'confirming';
    this.failure = null;
    try {
      const result = await this.deps.gateway.confirmCardChange(
        tokenId,
        details,
      );
      return epoch === this.epoch
        ? this.settle(result, subject, sender)
        : FAILED;
    } catch (error) {
      if (epoch === this.epoch) {
        await this.handleError(
          cardChangeErrorOutcome('confirm', asManageError(error)),
          subject,
          epoch,
        );
      }
      return FAILED;
    }
  }

  /** An action goes only to the form that sent the request (any form for a bank return); a replaced form ends on "not confirmed". */
  private settle(
    result: MembershipCardChangeResult,
    subject: CardChangeSubject,
    sender: CardForm | null,
  ): CardFormReply {
    this.inFlight = false;
    if (result.status === 'action_required') {
      if (
        !this.form ||
        (sender !== null && sender !== this.form) ||
        result.tokenId === null
      ) {
        this.fail('not_confirmed');
        return FAILED;
      }
      this.phase = 'challenge';
      this.tokenId = result.tokenId;
      if (result.action?.type === 'redirect') {
        this.deps.markers.saveCardRedirect({
          tokenId: result.tokenId,
          planCode: subject.planCode,
          wasPastDue: subject.wasPastDue,
        });
      }
      return toCardChangeFormReply(result);
    }
    if (result.status === 'pending' && subject.wasPastDue) {
      this.finish('restoring', subject, 'pending');
      this.deps.onRestorePending(null);
    } else {
      this.finish(
        'updated',
        subject,
        result.status === 'pending' ? 'pending' : 'updated',
      );
      this.deps.onCardUpdated(
        {
          card:
            result.status === 'updated'
              ? (result.membership?.card ?? null)
              : null,
          me: null,
        },
        subject.wasPastDue && result.membership?.status === 'active',
      );
    }
    this.forgetIfLeft();
    return toCardChangeFormReply(result);
  }

  private async handleError(
    outcome: CardChangeErrorOutcome,
    subject: CardChangeSubject,
    epoch: number,
  ): Promise<void> {
    if (outcome.incident) {
      this.deps.report(outcome.incident);
    }
    if (outcome.refresh) {
      this.deps.onMembershipChanged();
    }
    if (outcome.next === 'reconcile') {
      await this.reconcile(subject, epoch, outcome);
      return;
    }
    this.fail(outcome.failure);
  }

  /**
   * Checks a failed request through `/me`; never re-sends anything. An
   * overdue membership that turned `active` is a success. When the answer to
   * an overdue card change was lost and `/me` is still `past_due` (or did not
   * answer), the backend may have taken the card and be charging it: a fresh
   * form would invite a second charge, so the wait goes to the restoration
   * poll. Anything else — a refused request, or a membership that was not
   * overdue, where a replaced token leaves no trace — ends on a new form; a
   * second card change outside `past_due` charges nothing.
   */
  private async reconcile(
    subject: CardChangeSubject,
    epoch: number,
    outcome: CardChangeErrorOutcome,
  ): Promise<void> {
    this.inFlight = true;
    this.phase = 'reconciling';
    const membership = await this.deps.gateway
      .getMe()
      .catch((): undefined => undefined);
    if (epoch !== this.epoch) {
      return;
    }
    runInAction(() => {
      this.inFlight = false;
      if (subject.wasPastDue && membership?.status === 'active') {
        this.finish('updated', subject, 'updated');
        this.deps.onCardUpdated(
          { card: membership.card ?? null, me: membership },
          true,
        );
        this.forgetIfLeft();
        return;
      }
      if (
        subject.wasPastDue &&
        outcome.unknown &&
        (membership === undefined || membership?.status === 'past_due')
      ) {
        this.awaitRestoration(membership ?? null);
        return;
      }
      this.fail(outcome.failure);
    });
  }

  /** Hands an unconfirmed overdue card change to the membership page's restoration poll; not tracked as updated. */
  private awaitRestoration(membership: Membership | null): void {
    this.phase = 'idle';
    this.failure = null;
    this.exit = 'restoring';
    this.tokenId = null;
    this.deps.markers.clearCardRedirect();
    this.deps.onRestorePending(membership);
    this.forgetIfLeft();
  }

  private finish(
    exit: MembershipCardChangeExit,
    subject: CardChangeSubject,
    status: 'updated' | 'pending',
  ): void {
    this.phase = 'idle';
    this.failure = null;
    this.exit = exit;
    this.tokenId = null;
    this.deps.markers.clearCardRedirect();
    this.deps.track({
      name: 'cardUpdated',
      payload: {
        planCode: subject.planCode,
        wasPastDue: subject.wasPastDue,
        status,
      },
    });
  }

  /** Ends on `failure` with a fresh form; a redirect that never happened leaves no token behind. */
  private fail(failure: MembershipCardChangeFailure): void {
    this.failure = failure;
    this.trackFailure(failure);
    this.deps.markers.clearCardRedirect();
    this.renewForm();
    this.forgetIfLeft();
  }

  private forgetIfLeft(): void {
    if (this.isLeft && !this.inFlight) {
      this.forget();
    }
  }

  private trackFailure(failure: MembershipCardChangeFailure): void {
    if (this.subject) {
      this.deps.track({
        name: 'cardUpdateFailed',
        payload: { planCode: this.subject.planCode, reason: failure },
      });
    }
  }

  private renewForm(): void {
    this.unmountForm();
    this.inFlight = false;
    this.tokenId = null;
    this.phase = 'form';
    this.formKey += 1;
  }

  private handleFormError(form: CardForm, error: unknown): void {
    if (form !== this.form || this.inFlight) {
      return;
    }
    this.deps.report(formRuntimeIncident(error));
    if (this.phase === 'challenge') {
      this.fail('not_confirmed');
      return;
    }
    this.failure = 'form_unavailable';
    this.trackFailure('form_unavailable');
  }
}
